import { Stack, Text } from '@fluentui/react'
import { Bar } from 'react-chartjs-2'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip } from 'chart.js'
import { OutcomeCount } from '../../api/models'
import PerformancePanel from './PerformancePanel'
import { OUTCOME_GROUPS, OutcomeGroup, outcomeGroup, outcomeLabel } from './performanceLabels'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip)

type OutcomesChartProps = {
  outcomes: OutcomeCount[]
}

const GROUP_ORDER: OutcomeGroup[] = ['success', 'refused', 'failure']

const sortByGroupThenCount = (outcomes: OutcomeCount[]): OutcomeCount[] =>
  [...outcomes].sort(
    (a, b) =>
      GROUP_ORDER.indexOf(outcomeGroup(a.status)) - GROUP_ORDER.indexOf(outcomeGroup(b.status)) || b.count - a.count
  )

const countInGroup = (outcomes: OutcomeCount[], group: OutcomeGroup): number =>
  outcomes.filter((outcome) => outcomeGroup(outcome.status) === group).reduce((sum, outcome) => sum + outcome.count, 0)

const describeOutcomes = (outcomes: OutcomeCount[]): string =>
  'Answer outcomes. ' +
  outcomes
    .map((outcome) => `${outcomeLabel(outcome.status)} (${OUTCOME_GROUPS[outcomeGroup(outcome.status)].label}): ${outcome.count}`)
    .join('; ')

const options = {
  indexAxis: 'y' as const,
  responsive: true,
  maintainAspectRatio: false,
  plugins: { legend: { display: false } },
  scales: { x: { beginAtZero: true, ticks: { precision: 0 } } },
}

const GroupLegend = () => (
  <Stack horizontal wrap tokens={{ childrenGap: 16 }}>
    {GROUP_ORDER.map((group) => (
      <Stack key={group} horizontal verticalAlign="center" tokens={{ childrenGap: 6 }}>
        <span
          aria-hidden="true"
          style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: OUTCOME_GROUPS[group].color }}
        />
        <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
          {OUTCOME_GROUPS[group].label}
        </Text>
      </Stack>
    ))}
  </Stack>
)

const RefusedCaption = ({ outcomes }: OutcomesChartProps) => {
  const refused = countInGroup(outcomes, 'refused')
  const total = outcomes.reduce((sum, outcome) => sum + outcome.count, 0)
  const share = total > 0 ? Math.round((refused / total) * 100) : 0
  return (
    <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
      {refused.toLocaleString()} of {total.toLocaleString()} recorded outcomes ({share}%) returned a success response but were
      refused (no answer, privacy block or governance block).
    </Text>
  )
}

const OutcomesChart = ({ outcomes }: OutcomesChartProps) => {
  const sorted = sortByGroupThenCount(outcomes.filter((outcome) => outcome.count > 0))

  const chartData = {
    labels: sorted.map((outcome) => outcomeLabel(outcome.status)),
    datasets: [
      {
        label: 'Answers',
        data: sorted.map((outcome) => outcome.count),
        backgroundColor: sorted.map((outcome) => OUTCOME_GROUPS[outcomeGroup(outcome.status)].color),
        borderRadius: 3,
      },
    ],
  }

  return (
    <PerformancePanel
      title="Outcomes" isEmpty={sorted.length === 0}
      emptyMessage="No answer outcomes recorded for this period."
    >
      <GroupLegend />
      <div style={{ height: Math.max(160, sorted.length * 32) }}>
        <Bar data={chartData} options={options} role="img" aria-label={describeOutcomes(sorted)} />
      </div>
      <RefusedCaption outcomes={sorted} />
    </PerformancePanel>
  )
}

export default OutcomesChart
